import { camelCase, intersection, startCase } from "lodash";
import { createSelector } from "reselect";

import { createLookupCombiner, createLookupSelectors } from "./lib";

import { Species } from "constants/species";

import { resolveValue } from "lib/mlaSupplementaryData";
import { getBestMatchingProductId } from "lib/products";

import { MLAValidatedFields } from "constants/mla";
import { MarketDesignations, SaleType } from "constants/sale";
import { getCurrentSaleSubType } from "./saleSubTypes";
import { getCurrentSaleyard } from "./currentSale";
import { getMLASupplementaryData, getSaleLots, getSaleSubTypes } from "./root";
import { getProductsBySpecies } from "./products";

const getRequiredMLAFields = (saleSubType: SaleSubType) => {
  const isPrime = saleSubType.marketDesignation === MarketDesignations.PRIME;
  const isStore = saleSubType.marketDesignation === MarketDesignations.STORE;
  const isCattle = saleSubType.speciesId === Species.CATTLE;
  const isSheep = saleSubType.speciesId === Species.SHEEP;

  // MLA indicators only for prime or store sales, and only cattle/sheep saleyard sales
  if (
    !(
      saleSubType.saleType === SaleType.SALEYARD_AUCTION &&
      (isSheep || isCattle) &&
      (isPrime || isStore)
    )
  ) {
    return [];
  }

  // Validate product for all
  const requiredFields = [MLAValidatedFields.PRODUCT];

  // Validate age only sheep store sales
  if (isSheep && isStore) {
    requiredFields.push(MLAValidatedFields.AGE);
  }

  // Validate breed for cattle and sheep store sales
  if (isStore) {
    requiredFields.push(MLAValidatedFields.BREED);
  }

  // Validate live weight only for cattle sales
  if (isCattle) {
    requiredFields.push(MLAValidatedFields.EST_LIVE_WEIGHT);
  }

  // Validate carcass weight only for cattle store and sheep prime sales
  if ((isCattle && isStore) || (isSheep && isPrime)) {
    requiredFields.push(MLAValidatedFields.EST_CARCASS_WEIGHT);
  }

  // Validate joining and frame structure only for sheep store sales
  if (isSheep && isStore) {
    requiredFields.push(MLAValidatedFields.FRAME_STRUCTURE);
    requiredFields.push(MLAValidatedFields.JOINED_START);
    requiredFields.push(MLAValidatedFields.JOINED_END);
  }

  return requiredFields.map(f => camelCase(f));
};

export const getRequiredMLAFieldsBySaleSubType = createSelector(
  [getCurrentSaleSubType],
  (currentSaleSubType: SaleSubType) => getRequiredMLAFields(currentSaleSubType),
);

const generateMLAWarnings = (suppData, requiredFields, products) => {
  // ProductId is a calculated field based on lot attributes (breed, age, sex)
  // Include it when resolving values for indicator warnings

  // Normalize saleLot to snake_case for getBestMatchingProductId,
  // as the MLA data comes in as camel case, but a normal lot does not
  const normalizedSaleLot = {
    ...suppData.saleLot,
    breed_id: suppData.saleLot.breedId || suppData.saleLot.breed_id,
    age_id: suppData.saleLot.ageId || suppData.saleLot.age_id,
    sex_id: suppData.saleLot.sexId || suppData.saleLot.sex_id,
  };

  const productId = getBestMatchingProductId(normalizedSaleLot, products);

  return requiredFields.reduce((warnings, field) => {
    const value = resolveValue({ ...suppData, productId }, field, "");
    if (!value) {
      warnings.push(startCase(field));
    }
    return warnings;
  }, []);
};

export const selectMLAFieldWarningsBySaleLotId = createSelector(
  [
    getMLASupplementaryData,
    getRequiredMLAFieldsBySaleSubType,
    getProductsBySpecies,
  ],
  (
    mlaSupplementaryDataLookup: LookupSelectorResult<MLASupplementaryData>,
    requiredMLAFields,
    products,
  ) =>
    Object.values(mlaSupplementaryDataLookup).reduce(
      (acc, mlaSupplementaryData) => {
        acc[mlaSupplementaryData.saleLot.id] = generateMLAWarnings(
          mlaSupplementaryData,
          requiredMLAFields,
          products,
        );
        return acc;
      },
      {},
    ),
);

// For users with data indicators permission but not MLA permission
// Creates blank MLA data objects with sale lots nested for validation
export const selectDataFieldWarningsBySaleLotId = createSelector(
  [getSaleLots, getRequiredMLAFieldsBySaleSubType, getProductsBySpecies],
  (saleLots: LookupSelectorResult<SaleLot>, requiredMLAFields, products) =>
    Object.values(saleLots).reduce((acc, saleLot) => {
      // Create MLA data structure with camelCase fields for resolveValue compatibility
      const mlaData = {
        saleLot,
        ageId: saleLot.age_id,
        breedId: saleLot.breed_id,
        sexId: saleLot.sex_id,
      };
      acc[saleLot.id] = generateMLAWarnings(
        mlaData,
        requiredMLAFields,
        products,
      );
      return acc;
    }, {}),
);

export const getDefaultRequiredMLAFields = createSelector(
  [getRequiredMLAFieldsBySaleSubType],
  requiredFields => requiredFields.map(field => startCase(field)),
);

export const getCurrentSaleSubTypeIsMlaEnabled = createSelector(
  [getCurrentSaleSubType, getCurrentSaleyard],
  (currentSaleSubType, currentSaleyard) =>
    currentSaleyard?.sendMlaAnonymousDataSaleSubTypesIds?.includes(
      currentSaleSubType?.id,
    ),
);

const computeMLAValidatedSections = (saleSubType: SaleSubType) => {
  const requiredMLAFields = getRequiredMLAFields(saleSubType)?.map(field =>
    startCase(field),
  );
  return {
    mlaValidateWeightSection:
      intersection(requiredMLAFields, [
        MLAValidatedFields.EST_LIVE_WEIGHT,
        MLAValidatedFields.EST_CARCASS_WEIGHT,
      ]).length > 0,
    mlaValidateJoiningSection:
      intersection(requiredMLAFields, [
        MLAValidatedFields.JOINED_END,
        MLAValidatedFields.JOINED_START,
      ]).length > 0,
    mlaValidateFrameSection:
      intersection(requiredMLAFields, [MLAValidatedFields.FRAME_STRUCTURE])
        .length > 0,
    mlaValidateAge:
      intersection(requiredMLAFields, [MLAValidatedFields.AGE]).length > 0,
  };
};

const defaultMLAValidatedSections = {
  mlaValidateWeightSection: false,
  mlaValidateJoiningSection: false,
  mlaValidateFrameSection: false,
  mlaValidateAge: false,
};

type MLAValidatedSections = ReturnType<typeof computeMLAValidatedSections>;

export const [
  selectMLAValidatedSectionsBySaleSubTypeIdLookup,
  getMLAValidatedSectionsBySaleSubTypeId,
] = createLookupSelectors(
  [getSaleSubTypes],
  createLookupCombiner(computeMLAValidatedSections),
  defaultMLAValidatedSections,
) as [
  (state: State) => Record<string | number, MLAValidatedSections>,
  (id: number | null) => (state: State) => MLAValidatedSections,
];
